// What a switch's first configuration asks, in an operator's words (#2254,
// ADR-0538). The review and the confirm step both draw from here, so one line
// is said one way wherever it appears (ADR-0436 decision 4a).
//
// Restates ADR-0538 decisions 1, 2 and 4 — the values a first configuration takes
// from the corpus are said as one summary, counted by configuration path and named
// in plain words served by the API, and the apply's one click accepts every one of
// them; disagreements with the imported configuration are one line per field and
// pair of values, their ports named as ranges of printed port numbers, saying the
// switch's own value is what is sent; and a removed setting whose effective value
// does not change is said in the summary and asks nothing.
import type { ReactNode } from "react";
import type { components } from "../../api/schema";
import { plural } from "../../dashboard/format";
import { vendorNumber } from "./DeviceFace";

type View = components["schemas"]["SwitchFirstDocumentView"];
type Field = components["schemas"]["SwitchRunningConfigurationField"];
type Unchanged = components["schemas"]["SwitchFirstDocumentUnchanged"];

/** "port duplex, PoE detection and SSH": a list in prose. */
export function listed(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The corpus summary: how many settings the switch does not report, on its
 * model, take the standard values, and which. Undefined where there are none. */
export function corpusSummary(first: View, model: string | undefined): string | undefined {
  // A server from before ADR-0538 serves no count or names: the summary then
  // counts the elements and names them as served, and never disappears.
  const n = first.corpus_paths ?? first.acceptances.length;
  if (n === 0) return undefined;
  const names = first.corpus_settings ?? first.acceptances;
  const on = model ? ` for the ${model}` : "";
  return `${n} ${plural(n, "setting")} this switch doesn't report will be set to the standard ${plural(n, "value")}${on}: ${listed(names)}.`;
}

/** How many standard values the click accepts, counted as the summary counts them. */
export function corpusCount(first: View | undefined): number {
  return first ? (first.corpus_paths ?? first.acceptances.length) : 0;
}

/** Gabriel Provencher's wording, ruled on #2254 (note 4135606941): an inheritance
 * that is all a removed setting takes off. */
export function unchangedSentence(u: Unchanged): string {
  return `VLAN ${u.vlan} currently follows the switch's global IGMP version (${u.version}). After applying it will have its own IGMP version set explicitly instead. If someone later changes the switch's global version, VLAN ${u.vlan} won't follow.`;
}

/** The port an element names, as `ethernet[<port>]` at its start. */
const PORT = /^ethernet\[([^\]]+)\]$/;

/** Port numbers as ranges, "ports 1–8 and 10"; one port is "port 3". */
export function portRanges(numbers: readonly number[]): string {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  const runs: string[] = [];
  for (let i = 0; i < sorted.length;) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j]! + 1) j++;
    runs.push(j > i ? `${sorted[i]}–${sorted[j]}` : `${sorted[i]}`);
    i = j + 1;
  }
  return `${sorted.length === 1 ? "port" : "ports"} ${listed(runs)}`;
}

/** One line of the disagreements: a field and its two values, and where. */
export interface DisagreementGroup {
  key: string;
  field: string;
  read: string;
  held: string;
  /** Every disagreement of the group, as served. */
  members: Field[];
  /** Where, in plain port names: "ports 1–8 and 10", or the elements where
   * one does not name a port the switch prints a number for. */
  where: string;
}

/** Disagreements grouped by (field, the switch's value, the imported value),
 * in the order first met. Every member is kept, so the set the apply accepts is
 * the served set. */
export function groupDisagreements(disagreements: readonly Field[]): DisagreementGroup[] {
  const groups = new Map<string, Field[]>();
  for (const d of disagreements) {
    const key = [d.field, d.read, d.held].join("\u0000");
    groups.set(key, [...(groups.get(key) ?? []), d]);
  }
  return [...groups.entries()].map(([key, members]) => {
    const first = members[0]!;
    const numbers: number[] = [];
    const others: string[] = [];
    for (const m of members) {
      const port = PORT.exec(m.element)?.[1];
      const n = port === undefined ? undefined : vendorNumber(port);
      if (n === undefined) others.push(m.element);
      else numbers.push(n);
    }
    const where = [numbers.length > 0 ? portRanges(numbers) : "", ...others].filter(Boolean);
    return { key, field: first.field, read: first.read, held: first.held, members, where: listed(where) };
  });
}

/** One disagreement group's sentence: what each side has, where, and what
 * applying does about it (ADR-0427 decision 7b: the read wins). */
export function DisagreementWords({ g }: { g: DisagreementGroup }): ReactNode {
  return (
    <>
      {g.field}: the switch runs <code>{g.read}</code> and the imported configuration has <code>{g.held}</code>, on {g.where}.
      {" "}Applying keeps the switch&rsquo;s <code>{g.read}</code>.
    </>
  );
}
